import { CatalogResourceType } from "@evergis/api";
import { useDebouncedCallback } from "@evergis/react";
import { InputProps } from "@evergis/ui";
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { layer } from "resources/layers";
import { getUsername } from "../../../../ducks/user";
import { project } from "../../../../resources/projects";
import { table } from "../../../../resources/tables";
import { Field } from "./Field";

const INPUT_DEBOUNCE = 200;

export type SystemNameFieldProps = InputProps & {
  resourceType: CatalogResourceType;
  noCheck?: boolean;
};

export const SystemNameField: FC<SystemNameFieldProps> = ({ resourceType, noCheck, onChange, ...inputProps }) => {
  const { t } = useTranslation("dialogs");

  const [error, setError] = useState("");

  const currentUser: string = useSelector(getUsername);

  const {
    data: layerData,
    error: layerError,
    refetch: getLayer,
  } = layer.use({
    input: `${currentUser}.${inputProps.value}`,
    waitInput: true,
    immediate: false,
  });

  const {
    data: tableData,
    error: tableError,
    refetch: getTable,
  } = table.use({
    input: `${currentUser}.${inputProps.value}`,
    waitInput: true,
    immediate: false,
  });

  const {
    data: projectData,
    error: projectError,
    refetch: getProject,
  } = project.use({
    input: `${currentUser}.${inputProps.value}`,
    waitInput: true,
    immediate: false,
  });

  const resourceData = layerData || tableData || projectData;
  const resourceError = layerError || tableError || projectError;

  const getMethod = useMemo(() => {
    if (resourceType === CatalogResourceType.Layer) {
      return getLayer;
    }

    if (resourceType === CatalogResourceType.Table) {
      return getTable;
    }

    if (resourceType === CatalogResourceType.Map) {
      return getProject;
    }

    return () => null;
  }, [resourceType, getLayer, getTable, getProject]);

  const inputDebounce = useDebouncedCallback(INPUT_DEBOUNCE);

  const onChangeInput = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);

      setError("");

      if (e.target.value) {
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(e.target.value)) {
          setError(t("newLayer.invalidNameError"));
        }
      } else {
        setError(t("newLayer.emptyNameError"));
      }
    },
    [t, onChange, getMethod, inputDebounce],
  );

  useEffect(() => {
    if (!noCheck) {
      inputDebounce(async () => {
        await getMethod();
      });
    }
  }, [inputProps.value, noCheck]);

  useEffect(() => {
    if (resourceData) {
      setError(t("newLayer.duplicateNameError"));
    } else {
      setError(t(""));
    }
  }, [resourceError, resourceData, t]);

  return <Field {...inputProps} error={!noCheck && !!error} help={noCheck ? "" : error} onChange={onChangeInput} />;
};
